"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Period = void 0;
const date_fns_1 = require("date-fns");
const subMinutes_1 = __importDefault(require("date-fns/subMinutes"));
const convertTZ_1 = require("./convertTZ");
const dateUtil_1 = require("./dateUtil");
const overlappingInMinutes_1 = require("./overlappingInMinutes");
const periodJoiner_1 = require("./periodJoiner");
class Period {
    constructor(start, end) {
        this.start = start;
        this.end = end;
    }
    /**
     * taregtのPeriodとAND演算を行う
     * @param target
     * @returns 積集合を表すPeriod。被りがない場合はnull
     */
    and(target) {
        if (!(0, dateUtil_1.isOverlap)(this, target)) {
            return null;
        }
        const start = (0, dateUtil_1.getAfter)(this.start, target.start);
        const end = (0, dateUtil_1.getBefore)(this.end, target.end);
        return new Period(start, end);
    }
    /**
     * targetのPeriodとOR演算を行う
     * 被りの時間がlengthMin(取りたい時間の長さ)
     * より長い場合は、どこでも取れるのでくっつける
     * NOTE: 再起的なOR演算についてあとで考えんとあかんくなりそう
     * @param target
     * @param lengthMin
     * @returns 演算結果のPeriod
     */
    or(target, lengthMin) {
        const overlappingMinutes = (0, overlappingInMinutes_1.overlappingInMinutes)(this, target);
        if (overlappingMinutes < lengthMin) {
            return [this, target];
        }
        return periodJoiner_1.PeriodJoiner.joinPeriod(this, target);
    }
    /**
     * targetのPeriodとNOT演算を行う
     * @param target
     */
    not(target) {
        if (!(0, dateUtil_1.isOverlap)(this, target)) {
            return this;
        }
        const isStartWithIn = (0, date_fns_1.isWithinInterval)(this.start, {
            start: target.start,
            end: target.end
        });
        const isEndWithIn = (0, date_fns_1.isWithinInterval)(this.end, {
            start: target.start,
            end: target.end
        });
        //   a
        //  |-|
        //   b
        // 12  13
        // |---|
        //   none
        if (isStartWithIn && isEndWithIn) {
            return null;
        }
        //   a
        // |---|
        //     b
        //   12  13
        //   |---|
        // |-|
        if (isEndWithIn) {
            return new Period(this.start, target.start);
        }
        //     a
        //   |---|
        //   b
        // 12  13
        // |---|
        //     |-|
        if (isStartWithIn) {
            return new Period(target.end, this.end);
        }
        //       a
        //   |-------|
        //       b
        //     12  13
        //     |---|
        //   |-|   |-|
        const periods = [
            new Period(this.start, target.start),
            new Period(target.end, this.end)
        ];
        return periods;
    }
    lengthInMinutes() {
        return (0, date_fns_1.differenceInMinutes)(this.end, this.start);
    }
    lengthInDays() {
        return (0, date_fns_1.differenceInDays)(this.end, this.start);
    }
    slideMinutes(minutes) {
        return new Period((0, date_fns_1.addMinutes)(this.start, minutes), (0, date_fns_1.addMinutes)(this.end, minutes));
    }
    convertTZ(originTz, targetTz) {
        return new Period((0, convertTZ_1.convertTZ)(this.start, originTz, targetTz), (0, convertTZ_1.convertTZ)(this.end, originTz, targetTz));
    }
    // 指定した分で時間幅を狭めるBufferに利用
    // ex) 30分の場合
    // 12: 00
    //         =>    12: 30
    //               13: 30
    // 14: 00
    narrowDownMinutes(minutes) {
        const narrowDownedStart = (0, date_fns_1.addMinutes)(this.start, minutes);
        const narrowDownedEnd = (0, subMinutes_1.default)(this.end, minutes);
        if (narrowDownedStart >= narrowDownedEnd) {
            return null;
        }
        return new Period((0, date_fns_1.addMinutes)(this.start, minutes), (0, subMinutes_1.default)(this.end, minutes));
    }
    // 指定した分で時間幅を広げる。
    // ↑の逆
    expandMinutes(minutes) {
        const expandMinutesStart = (0, subMinutes_1.default)(this.start, minutes);
        const expandMinutesEnd = (0, date_fns_1.addMinutes)(this.end, minutes);
        return new Period(expandMinutesStart, expandMinutesEnd);
    }
    /**
     * thisが引数のPeriodのSubsetかどうか
     * @param period
     * @returns
     *
     * Subsetについて
     * @see https://www.nli-research.co.jp/report/detail/id=63317?site=nli
     */
    isSubsetOf(period) {
        return ((0, date_fns_1.isWithinInterval)(this.start, period) && (0, date_fns_1.isWithinInterval)(this.end, period));
    }
    /**
     * thisが引数のPeriodのSupersetかどうか
     * @param period
     * @returns
     */
    isSupersetOf(period) {
        // 逆にしてる
        return period.isSubsetOf(this);
    }
    /**
     * 同じ時間帯かどうか
     * @param period
     * @returns
     */
    isTheSame(period) {
        return (this.start.getTime() === period.start.getTime() &&
            this.end.getTime() === period.end.getTime());
    }
    /**
     * プレーンなオブジェクトに変換
     * @returns
     */
    get planeObject() {
        const { start, end } = this;
        return {
            start,
            end
        };
    }
    /**
     * タイムスタンプから生成
     * @param timeStampPeriod
     * @returns
     */
    static fromTimeStamp(timeStampPeriod) {
        return new Period(timeStampPeriod.start.toDate(), timeStampPeriod.end.toDate());
    }
    /**
     * 開始時間と長さからPeriodを生成する
     * @param start
     * @param durationMin
     * @returns
     */
    static fromStartAndDurationMin(start, durationMin) {
        return new Period(start, (0, date_fns_1.addMinutes)(start, durationMin));
    }
}
exports.Period = Period;
//# sourceMappingURL=period.js.map